import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { AvailableViews } from '@apparatix/types';
import { useFetch, useToastNotifications, useUtilities } from '@apparatix/composables';
import type { Relationship, RelationshipType } from '@apparatix/schemas/Relationship';
import { AssociationsResponse, accountToRelationship, contactToRelationship, mapCampaignsToIndividualTypes } from '@apparatix/schemas/Relationship';
import { AssociatedAccount } from '@apparatix/schemas/Account';
import { AssociatedCampaign } from '@apparatix/schemas/Campaign';
import { AssociatedContact } from '@apparatix/schemas/Contact';

type ViewsWithRelationships = Extract<AvailableViews, 'accounts' | 'contacts' | 'leads' | 'opportunities'>;

type RelationshipRecord = {
  [K in RelationshipType]?: Relationship[];
}

type CachedRelationship = Record<number, RelationshipRecord>;

type Cache = Record<ViewsWithRelationships, CachedRelationship>;


export const useCrmRelationshipsStore = defineStore('crm-relationships', () => {
  const { get } = useFetch();
  const { showError } = useToastNotifications();
  const { arrayHasValues, developerWarning } = useUtilities();

  const cache = ref<Cache>({
    accounts: {},
    contacts: {},
    leads: {},
    opportunities: {},
  });

  const isLoading = ref(false);

  const mapAccounts = (accounts: AssociatedAccount[]) => {
    if (!arrayHasValues(accounts)) return [];

    try {
      return accounts
        .filter(account => account.c2Status?.toLowerCase() !== 'inactive')
        .map(account => accountToRelationship.parse(account)) satisfies Relationship[];
    } catch (e) {
      developerWarning(true, e);
      return [];
    }
  };

  const mapCampaigns = (campaigns: AssociatedCampaign[]) => {
    const empty = {
      Lead: [],
      Opportunity: [],
    };

    if (!arrayHasValues(campaigns)) {
      return empty;
    }

    try {
      return mapCampaignsToIndividualTypes.parse(campaigns);
    } catch (e) {
      developerWarning(true, e);
      return empty;
    }
  };

  const mapContacts = (contacts: AssociatedContact[]) => {
    if (!arrayHasValues(contacts)) return [];

    try {
      return contacts.map(contact => contactToRelationship.parse(contact)) satisfies Relationship[];
    } catch (e) {
      developerWarning(true, e);
      return [];
    }
  };

  const loadRelationships = async (view: ViewsWithRelationships, parentId: number) => {
    const page = view === 'leads' || view === 'opportunities'
      ? 'opportunities'
      : view;

    const url = `/crm/${page}/${parentId}/associations`;

    try {
      isLoading.value = true;
      const response = await get<AssociationsResponse>(url);

      cache.value[view][parentId] = {
        Account: mapAccounts(response?.data?.accounts),
        Contact: mapContacts(response?.data?.contacts),
        ...mapCampaigns(response?.data?.campaigns),
      };
    } catch {
      showError('Unable to load associations');
    }
    isLoading.value = false;
  };

  const getRelationships = async (view: ViewsWithRelationships, parentId: number, forceLoad: boolean = false) => {
    if (!(parentId in cache.value[view]) || forceLoad) {
      await loadRelationships(view, parentId);
    }

    return cache.value[view][parentId];
  };

  const initializeRelationshipsRef = (): RelationshipRecord => ({
    Account: [],
    Lead: [],
    Contact: [],
    Opportunity: [],
  });

  return {
    getRelationships,
    isLoading,
    initializeRelationshipsRef,
  };
});
